import * as THREE from "three";
import { SHOW_DEV_TOOLS } from "../../config.js";
import { buildScrewPath } from "../../utils/geometry.js";
import {
  FADE_SLIDE_OFFSET_Y,
  VARIANT_TWEEN_MS,
  VARIANT_STEP_MS,
  MOTION_SPLIT,
  easeCubic,
} from "./tweenTiming.js";

// Variant transition engine (rest poses, staged node tweens, fades); installed on
// FurnitureElement (statics) and its prototype.
export const variantTweenStatics = {
  // Pose per step: stage k = `seedPose` overlaid by steps 0..k, so every stage is absolute
  // and replayable backwards. Seed with the far side's rest pose, never bind — a step names
  // only the channels it moves, so bind would snap every undeclared node to variant 0.
  _accumulateSteps(steps, seedPose = []) {
    const merged = new Map();
    for (const a of seedPose) {
      if (a.node) merged.set(a.node, { ...a });
    }
    return (steps ?? []).map((step) => {
      for (const a of step.nodes ?? []) {
        if (!a.node) continue;
        merged.set(a.node, { ...(merged.get(a.node) ?? {}), ...a });
      }
      return [...merged.values()];
    });
  },

  /**
   * Steps for one transition, most specific first: a path keyed to this exact pair beats a
   * variant's default, and forward beats reverse within each tier. `reverseSteps: false`
   * opts a variant out of being replayed backwards.
   * @returns {{steps:Array, reversed:boolean}|null}
   */
  _resolveStepPath(fromVariant, toVariant) {
    const canReverse = fromVariant?.reverseSteps !== false;
    const pairIn = toVariant?.animationStepsFrom?.[fromVariant?.name];
    if (pairIn?.length) return { steps: pairIn, reversed: false };
    const pairOut = fromVariant?.animationStepsFrom?.[toVariant?.name];
    if (pairOut?.length && canReverse) return { steps: pairOut, reversed: true };
    if (toVariant?.animationSteps?.length) return { steps: toVariant.animationSteps, reversed: false };
    if (fromVariant?.animationSteps?.length && canReverse) {
      return { steps: fromVariant.animationSteps, reversed: true };
    }
    return null;
  },
};

export const variantTweenMethods = {
  _collectAnimatedNodes() {
    const variants = this.furnitureData?.properties?.variants;
    if (!variants?.length || !this.mesh) return;

    const names = new Set();
    for (const v of variants) {
      for (const a of v.nodeAnimations ?? []) {
        if (a.node) names.add(a.node);
      }
      // A node may move only mid-sequence and end up back at bind in the rest pose.
      for (const step of [v.animationSteps ?? [], ...Object.values(v.animationStepsFrom ?? {})].flat()) {
        for (const a of step.nodes ?? []) {
          if (a.node) names.add(a.node);
        }
      }
    }
    if (names.size === 0) return;

    this.mesh.traverse((child) => {
      if (child.name && names.has(child.name) && !this._animatedNodes.has(child.name)) {
        this._animatedNodes.set(child.name, {
          node: child,
          bindPos: child.position.clone(),
          bindQuat: child.quaternion.clone(),
          bindVisible: child.visible,
        });
      }
    });
  },

  _applyVariantNodeStateInstant(variantIndex) {
    if (this._animatedNodes.size === 0) return;
    const variant = this.furnitureData?.properties?.variants?.[variantIndex];
    if (!variant) return;

    const restPose = variant.nodeAnimations ?? [];
    const tmpEuler = new THREE.Euler();
    for (const info of this._animatedNodes.values()) {
      const { toPos, toQuat, toVisible } = this._resolveNodeTargetState(restPose, info, tmpEuler);
      info.node.position.copy(toPos);
      info.node.quaternion.copy(toQuat);
      info.node.visible = toVisible;
    }
  },

  /**
   * Resolve a node's target {position, quaternion, visible, motion} for a pose list.
   * Returns the declared override when the list mentions the node, otherwise
   * the captured bind pose. Position/quaternion are returned via scratch refs
   * owned by `info` (bindPos/bindQuat) or freshly built — callers must clone
   * before mutating if they need persistence.
   */
  _resolveNodeTargetState(poseList, info, scratchEuler) {
    const target = this._resolveNodeTarget(poseList, info.node.name);
    if (!target) {
      return { toPos: info.bindPos, toQuat: info.bindQuat, toVisible: info.bindVisible };
    }
    const toPos = target.position
      ? new THREE.Vector3(target.position.x, target.position.y, target.position.z)
      : info.bindPos;
    let toQuat;
    if (target.rotation) {
      scratchEuler.set(
        THREE.MathUtils.degToRad(target.rotation.x ?? 0),
        THREE.MathUtils.degToRad(target.rotation.y ?? 0),
        THREE.MathUtils.degToRad(target.rotation.z ?? 0),
      );
      toQuat = new THREE.Quaternion().setFromEuler(scratchEuler);
    } else {
      toQuat = info.bindQuat;
    }
    return { toPos, toQuat, toVisible: target.visible !== false, motion: target.motion };
  },

  /**
   * Apply the composite sub-model's local offset/rotation/visibility for the given variant.
   */
  _applyVariantCompositeTransform(variantIndex) {
    if (!this.compositeMesh) return;
    const variants = this.furnitureData?.properties?.variants;
    const variant = variants?.[variantIndex];
    const visible = variant?.compositeVisible !== false; // default visible
    this.compositeMesh.visible = visible;
    if (visible) {
      const offset = variant?.compositeOffset || { x: 0, y: 0, z: 0 };
      const rotY = variant?.compositeRotationY ?? 0;
      this.compositeMesh.position.set(offset.x, offset.y, offset.z);
      this.compositeMesh.rotation.set(0, THREE.MathUtils.degToRad(rotY), 0);
    }
  },

  /**
   * Advance the active variant tween by one frame. Drives compositeMesh transform AND any
   * per-node transforms declared by variant.nodeAnimations (HingePivot rotation, Storage_GRP
   * translation, etc.).
   */
  _updateVariantTween(deltaTime) {
    const tw = this._tween;
    if (!tw) return;

    tw.elapsed += deltaTime;
    const t = tw.duration > 0 ? Math.min(tw.elapsed / tw.duration, 1) : 1;
    const ease = easeCubic(t);

    if (tw.composite && this.compositeMesh) {
      this.compositeMesh.position.lerpVectors(tw.composite.fromPos, tw.composite.toPos, ease);
      this.compositeMesh.rotation.y =
        tw.composite.fromRotY + (tw.composite.toRotY - tw.composite.fromRotY) * ease;
    }
    for (const n of tw.nodes) {
      // A sequenced leg splits the beat instead of blending the channels, which is what turns a
      // turn-plus-move into an inward spiral.
      let posEase = ease;
      let rotEase = ease;
      if (n.seqTurnFirst !== undefined) {
        const lead = easeCubic(Math.min(t / MOTION_SPLIT, 1));
        const trail = easeCubic(Math.max((t - MOTION_SPLIT) / (1 - MOTION_SPLIT), 0));
        rotEase = n.seqTurnFirst ? lead : trail;
        posEase = n.seqTurnFirst ? trail : lead;
      }

      if (n.arc) {
        // Turning the axis offset by the same angle the node turns is what makes the path a rigid swing.
        n.node.position.copy(n.arc.fromRel).applyAxisAngle(n.arc.axis, n.arc.angle * ease)
          .add(n.arc.center).addScaledVector(n.arc.axis, n.arc.slide * ease);
      } else if (n.animatePos) {
        n.node.position.lerpVectors(n.fromPos, n.toPos, posEase);
      }
      if (n.animateRot) n.node.quaternion.slerpQuaternions(n.fromQuat, n.toQuat, rotEase);
      // Flip visible on at t=0 for show, off at t=1 for hide so the fade is observable.
      if (n.toVisible !== undefined) {
        if (n.toVisible && !n.node.visible) n.node.visible = true;
        const factor = n.toVisible ? ease : 1 - ease;
        for (const ft of n.fadeTargets) {
          if (ft.isArray) {
            for (let i = 0; i < ft.fadeMaterial.length; i++) {
              ft.fadeMaterial[i].opacity = ft.origOpacity[i] * factor;
            }
          } else {
            ft.fadeMaterial.opacity = ft.origOpacity * factor;
          }
        }
        if (t >= 1) {
          this._restoreFadeTargets(n.fadeTargets);
          this._clearFadeMeta(n.node);
          if (!n.toVisible) n.node.visible = false;
        }
      }
    }
    for (const c of tw.components || []) {
      c.mesh.position.lerpVectors(c.fromPos, c.toPos, ease);
      c.mesh.rotation.y = c.fromRotY + (c.toRotY - c.fromRotY) * ease;
    }

    if (t >= 1) {
      this._tween = tw.nextTween;
      if (this._tween) this._beginStage(this._tween);
    }
  },

  // ===== MULTI-STEP VARIANT STAGES =====

  /**
   * Node-pose stages for a transition, settle leg (the target's rest pose) last. `step` is
   * the authored step a stage mirrors, or null where the system synthesized the leg.
   * @returns {Array<{durationMs:number, list:Array, step:number|null}>}
   */
  _buildNodeStages(fromVariant, toVariant) {
    const settle = {
      durationMs: toVariant?.transitionMs ?? VARIANT_TWEEN_MS,
      list: toVariant?.nodeAnimations ?? [],
      step: null,
    };

    const path = variantTweenStatics._resolveStepPath(fromVariant, toVariant);
    if (!path) return [settle];
    const { steps, reversed } = path;

    if (!reversed) {
      const acc = variantTweenStatics._accumulateSteps(steps, fromVariant?.nodeAnimations ?? []);
      return [
        ...acc.map((list, i) => ({ durationMs: steps[i].durationMs ?? VARIANT_STEP_MS, list, step: i })),
        settle,
      ];
    }

    // Each reverse leg takes the duration of the forward leg it undoes: the first undoes the
    // departing settle, then step k down to step 0 landing at `settle`. Seeded with the
    // arriving pose, so unwinding reveals it leg by leg.
    const acc = variantTweenStatics._accumulateSteps(steps, settle.list);
    const lists = [...acc.reverse(), settle.list];
    const durations = [
      fromVariant.transitionMs ?? VARIANT_TWEEN_MS,
      ...steps.map((s) => s.durationMs ?? VARIANT_STEP_MS).reverse(),
    ];
    // Leg 0 undoes the settle and is synthesized; the rest mirror authored steps.
    return lists.map((list, i) => ({
      durationMs: durations[i],
      list,
      step: i > 0 ? steps.length - i : null,
    }));
  },

  /**
   * Assemble `this._tween` from the composite and node stage lists and return the
   * tail record, or null when nothing moves. Intermediate composite stages run
   * first (the 180° routing), then the authored node steps, and the last stage of
   * each list is merged so both reach the target pose together.
   * @param {Array<{durationMs:number, composite:object}>} compositeStages
   * @param {Array<{durationMs:number, list:Array, step:number|null}>} nodeStages settle last
   * @returns {object|null} the tail tween record
   */
  _startTweenChain(compositeStages, nodeStages) {
    const settle = nodeStages[nodeStages.length - 1];
    const lastComposite = compositeStages[compositeStages.length - 1] ?? null;

    // `pendingList` is the node pose this stage builds its entries from when it
    // becomes current, so from-poses read live and fade clones aren't held early.
    const records = [];
    const push = (durationMs, composite, pendingList, step = null) => records.push({
      elapsed: 0, duration: durationMs / 1000, composite, nodes: [], components: null,
      pendingList, step, nextTween: null,
    });
    for (let i = 0; i < compositeStages.length - 1; i++) {
      push(compositeStages[i].durationMs, compositeStages[i].composite, null);
    }
    for (let i = 0; i < nodeStages.length - 1; i++) {
      push(nodeStages[i].durationMs, null, nodeStages[i].list, nodeStages[i].step);
    }
    push(lastComposite?.durationMs ?? settle.durationMs, lastComposite?.composite ?? null, settle.list);

    for (let i = 0; i + 1 < records.length; i++) records[i].nextTween = records[i + 1];

    const head = records[0];
    this._beginStage(head);
    if (records.length === 1 && this._stageIsIdle(head)) {
      // Abandon anything still in flight too — it is aimed at the variant we just left.
      this._tween = null;
      return null;
    }
    this._tween = head;
    return records[records.length - 1];
  },

  // Materialize a stage's node entries and start its clock. Only a synthesized idle leg is
  // zeroed; an authored step's empty beat may be deliberate, so warn instead of guessing.
  _beginStage(stage) {
    if (stage.pendingList) {
      stage.nodes = this._buildNodeTweenEntries(stage.pendingList);
      stage.pendingList = null;
    }
    if (this._stageIsIdle(stage)) {
      if (stage.step == null) stage.duration = 0;
      else if (SHOW_DEV_TOOLS) {
        console.warn(`[variant] ${this.furnitureData?.urlCode} step ${stage.step} moves nothing — holding ${Math.round(stage.duration * 1000)}ms`);
      }
    }
    stage.elapsed = 0;
  },

  _stageIsIdle(stage) {
    return stage.nodes.length === 0 && !stage.composite && !stage.components?.length;
  },

  /** The pose declared for `nodeName` in `poseList`, or null to mean "revert to bind". */
  _resolveNodeTarget(poseList, nodeName) {
    return poseList?.find(a => a.node === nodeName) ?? null;
  },

  /**
   * Build the per-node tween entries for a transition into `poseList`.
   * Considers every node referenced by any variant — nodes not mentioned by the
   * target pose tween back to bind pose.
   */
  _buildNodeTweenEntries(poseList) {
    const entries = [];
    if (this._animatedNodes.size === 0) return entries;

    const tmpEuler = new THREE.Euler();
    const tmpQuat = new THREE.Quaternion();
    const itemMotion = this.furnitureData?.properties?.motion ?? 'linear';

    for (const info of this._animatedNodes.values()) {
      const resolved = this._resolveNodeTargetState(poseList, info, tmpEuler);
      const toPos = resolved.toPos === info.bindPos ? info.bindPos.clone() : resolved.toPos;
      const toQuat = resolved.toQuat === info.bindQuat ? info.bindQuat.clone() : resolved.toQuat;
      const toVisible = resolved.toVisible;

      const fromPos = info.node.position.clone();
      const fromQuat = info.node.quaternion.clone();
      const animateVis = info.node.visible !== toVisible;

      // Visibility-flip rotation snap: hide-only variant entries have no
      // rotation override, so a bind-quat fallback would spin the node mid-fade.
      let effFromPos = fromPos;
      let effToPos = toPos;
      let effFromQuat = fromQuat;
      let effToQuat = toQuat;
      let fadeTargets = null;
      if (animateVis) {
        const slideLocal = this._computeLocalSlideOffset(info.node);
        if (toVisible) {
          effFromPos = toPos.clone().add(slideLocal);
          info.node.quaternion.copy(toQuat);
          effFromQuat = toQuat;
        } else {
          effToPos = fromPos.clone().add(slideLocal);
          effToQuat = fromQuat;
        }
        fadeTargets = this._buildFadeTargets(info.node);
        // Stamp the fade's target visibility + settled pose so dimension code measures at rest.
        this._setFadeMeta(info.node, toVisible, effToPos, effToQuat);
      }

      const animatePos = !effFromPos.equals(effToPos);
      const animateRot = !tmpQuat.copy(effFromQuat).equals(effToQuat);
      if (!animatePos && !animateRot && !animateVis) continue;

      // How a leg that both moves and turns routes its two channels: `arc` swings the part rigidly,
      // the sequenced modes give each channel its own beat, `linear` blends them (and spirals inward).
      let arc = null;
      let seqTurnFirst;
      if (animatePos && animateRot) {
        const motion = resolved.motion ?? itemMotion;
        if (motion === 'arc') arc = buildScrewPath(effFromPos, effFromQuat, effToPos, effToQuat);
        else if (motion === 'turn-then-move') seqTurnFirst = true;
        else if (motion === 'move-then-turn') seqTurnFirst = false;
      }

      entries.push({
        node: info.node,
        fromPos: effFromPos, toPos: effToPos,
        fromQuat: effFromQuat, toQuat: effToQuat,
        animatePos, animateRot, arc, seqTurnFirst,
        toVisible: animateVis ? toVisible : undefined,
        fadeTargets,
      });
    }
    return entries;
  },

  // World +Y * FADE_SLIDE_OFFSET_Y → node-parent local space, so the visible
  // slide is constant regardless of the parent's world rotation/scale.
  _computeLocalSlideOffset(node) {
    const out = new THREE.Vector3(0, FADE_SLIDE_OFFSET_Y, 0);
    const parent = node.parent;
    if (!parent) return out;
    parent.updateMatrixWorld();
    const parentQuat = parent.getWorldQuaternion(new THREE.Quaternion());
    const parentScale = parent.getWorldScale(new THREE.Vector3());
    out.applyQuaternion(parentQuat.invert());
    out.divide(parentScale);
    return out;
  },

  _buildFadeTargets(rootNode) {
    const targets = [];
    rootNode.traverse((child) => {
      if (!child.material) return;
      const orig = child.material;
      const isArray = Array.isArray(orig);
      if (isArray) {
        const fade = orig.map((m) => {
          const c = m.clone();
          c.transparent = true;
          return c;
        });
        targets.push({
          mesh: child,
          originalMaterial: orig,
          fadeMaterial: fade,
          origOpacity: orig.map((m) => m.opacity),
          isArray: true,
        });
        child.material = fade;
      } else {
        const fade = orig.clone();
        fade.transparent = true;
        targets.push({
          mesh: child,
          originalMaterial: orig,
          fadeMaterial: fade,
          origOpacity: orig.opacity,
          isArray: false,
        });
        child.material = fade;
      }
    });
    return targets;
  },

  // Fade bookkeeping on node.userData so DimensionManager can measure a fading node at its
  // settled pose (see freezeFadingNodesAtRest). Set/clear together so all keys stay in sync.
  _setFadeMeta(node, targetVisible, restPos, restQuat) {
    node.userData._fadeActive = true;
    node.userData._fadeTargetVisible = targetVisible;
    node.userData._fadeRestPos = restPos.clone();
    node.userData._fadeRestQuat = restQuat.clone();
  },

  _clearFadeMeta(node) {
    node.userData._fadeActive = false;
    delete node.userData._fadeTargetVisible;
    delete node.userData._fadeRestPos;
    delete node.userData._fadeRestQuat;
  },

  // Safe to call multiple times — fadeTargets is emptied on first run.
  _restoreFadeTargets(fadeTargets) {
    if (!fadeTargets) return;
    for (const ft of fadeTargets) {
      ft.mesh.material = ft.originalMaterial;
      if (ft.isArray) {
        for (const m of ft.fadeMaterial) m.dispose();
      } else {
        ft.fadeMaterial.dispose();
      }
    }
    fadeTargets.length = 0;
  },

  _cancelPendingFades() {
    let tw = this._tween;
    while (tw) {
      if (tw.nodes) {
        for (const n of tw.nodes) {
          if (n.fadeTargets) this._restoreFadeTargets(n.fadeTargets);
          if (n.node && n.toVisible !== undefined) this._clearFadeMeta(n.node);
        }
      }
      tw = tw.nextTween;
    }
  },
};
